'use client';
/**
 * Restore an album from a `.fab` backup.
 *
 * The dialog exists so an irreversible decision is made with its consequences on screen. It is built
 * around one rule: lead with the verdict, then show only what actually changes.
 *
 *  - inspect first (`POST /api/backup/inspect` writes nothing);
 *  - one sentence at the top answers "will this hurt?" before any detail;
 *  - the change list holds only fields that differ — an unchanged row is noise;
 *  - each choice is a card that states its own consequence, and overwriting past photo loss is
 *    gated behind an explicit acknowledgement rather than a lone red button.
 *
 * Backups carry every original photo, so they run to hundreds of megabytes: both requests go through
 * XHR (`postFileWithProgress`) to show a real percentage rather than an unfalsifiable spinner.
 */
import * as React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Copy, FileUp, PackageOpen, TriangleAlert } from 'lucide-react';
import { Button, Dialog, ProgressBar, toast } from '@freealbum/ui';
import type { BackupDiff } from '@freealbum/backup';
import { useRouter } from '@/i18n/navigation';
import { api, ApiError, BACKUP_ACCEPT, type BackupInspectResponse, type RestoreMode } from '@/lib/api';
import { useApiErrorMessage } from '@/hooks/use-api-error';
import { formatBytes, formatDate } from '@/lib/utils';

/** Album formats that have a `formats.*` label; anything else is shown raw rather than crashing. */
const FORMAT_KEYS: readonly string[] = [
  'sq15', 'sq20', 'sq25', 'sq30', 'a4p', 'a4l', 'r20x30', 'r30x20', 'r25x35', 'r40x30', 'custom',
];

type Step = 'pick' | 'inspecting' | 'ready' | 'restoring';

export interface RestoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RestoreDialog({ open, onOpenChange }: RestoreDialogProps): React.JSX.Element {
  const t = useTranslations('backup.restore');
  const tc = useTranslations('common');
  const tf = useTranslations('formats');
  const locale = useLocale();
  const rtl = locale === 'he';
  const router = useRouter();
  const errorMessage = useApiErrorMessage();

  const [step, setStep] = React.useState<Step>('pick');
  const [file, setFile] = React.useState<File | null>(null);
  const [info, setInfo] = React.useState<BackupInspectResponse | null>(null);
  const [error, setError] = React.useState<{ title: string; detail: string } | null>(null);
  /** 0–100 while bytes are moving, `null` once the server has them and is working. */
  const [progress, setProgress] = React.useState<number | null>(null);
  const [mode, setMode] = React.useState<RestoreMode>('copy');
  /** Ticked before an overwrite that destroys photos. Reset with every new file. */
  const [acknowledged, setAcknowledged] = React.useState(false);

  const abortRef = React.useRef<AbortController | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const verdictRef = React.useRef<HTMLParagraphElement>(null);
  const inputId = React.useId();
  const hintId = React.useId();
  const ackId = React.useId();

  const busy = step === 'inspecting' || step === 'restoring';

  const reset = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStep('pick');
    setFile(null);
    setInfo(null);
    setError(null);
    setProgress(null);
    setAcknowledged(false);
  }, []);

  // Closing throws the file away: a stale inspection would describe a diff that may no longer hold.
  React.useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // The verdict is the answer, so it is what focus (and a screen reader) lands on when it appears.
  React.useEffect(() => {
    if (step === 'ready') verdictRef.current?.focus();
  }, [step]);

  const formatLabel = (id: string) => (FORMAT_KEYS.includes(id) ? tf(id as 'sq20') : id);
  /** Never interpolate an empty title into a sentence — an album with no name still has a name here. */
  const titleOr = (value: string, fallback: string) => (value.trim() ? value : fallback);

  /**
   * The server explains a bad backup precisely ("not a freealbum backup file", "unsupported version"),
   * so that message is shown verbatim; the generic `errors.*` text is only the fallback.
   */
  const describe = (err: unknown, title: string) => ({
    title,
    detail: err instanceof ApiError ? (err.body?.error?.message ?? errorMessage(err)) : errorMessage(err),
  });

  const isAbort = (err: unknown) => err instanceof DOMException && err.name === 'AbortError';
  const onProgress = (fraction: number) => setProgress(fraction >= 1 ? null : fraction * 100);

  const inspect = async (picked: File) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setFile(picked);
    setInfo(null);
    setError(null);
    setAcknowledged(false);
    setProgress(0);
    setStep('inspecting');
    try {
      setInfo(await api.backup.inspect(picked, onProgress, controller.signal));
      setStep('ready');
    } catch (err) {
      setStep('pick');
      if (!isAbort(err)) setError(describe(err, t('error.inspect')));
    } finally {
      abortRef.current = null;
    }
  };

  const run = async (next: RestoreMode) => {
    if (!file) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setMode(next);
    setError(null);
    setProgress(0);
    setStep('restoring');
    try {
      const result = await api.backup.restore(file, next, onProgress, controller.signal);
      toast({
        title: result.mode === 'overwrite' ? t('done.overwrite') : t('done.copy'),
        description: [
          t('done.photos', { count: result.photosRestored }),
          result.photosReused > 0 ? t('done.reused', { count: result.photosReused }) : null,
        ]
          .filter(Boolean)
          .join(' '),
        variant: 'success',
        duration: 9000,
      });
      onOpenChange(false);
      router.push(`/albums/${result.albumId}/edit`);
    } catch (err) {
      setStep('ready');
      if (!isAbort(err)) setError(describe(err, t('error.restore')));
    } finally {
      abortRef.current = null;
    }
  };

  const phase =
    step === 'inspecting'
      ? progress === null
        ? t('reading')
        : t('uploading')
      : progress === null
        ? t(mode === 'overwrite' ? 'working.overwrite' : 'working.copy')
        : t('uploading');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (step !== 'restoring') onOpenChange(next);
      }}
      title={t('title')}
      description={step === 'pick' ? t('lead') : undefined}
      // Radix points `aria-describedby` at the lead; once it is gone the pointer has to go too.
      {...(step === 'pick' ? {} : { 'aria-describedby': undefined })}
      size="lg"
      hideClose={step === 'restoring'}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        inputRef.current?.focus();
      }}
      onEscapeKeyDown={(e) => {
        if (step === 'restoring') e.preventDefault();
      }}
      footer={
        step === 'restoring' ? null : (
          <Button
            variant="ghost"
            onClick={() => (step === 'inspecting' ? abortRef.current?.abort() : onOpenChange(false))}
          >
            {tc('cancel')}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {step === 'pick' ? (
          <>
            <div className="relative rounded-xl border-2 border-dashed border-line-strong bg-paper-2/60 transition-colors focus-within:border-accent focus-within:bg-accent-soft/50">
              <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept={BACKUP_ACCEPT}
                aria-describedby={hintId}
                className="absolute inset-0 size-full cursor-pointer opacity-0"
                onChange={(e) => {
                  const picked = e.target.files?.[0];
                  e.target.value = '';
                  if (picked) void inspect(picked);
                }}
              />
              <label htmlFor={inputId} className="pointer-events-none flex flex-col items-center gap-2 px-6 py-10 text-center">
                <FileUp aria-hidden className="size-8 text-ink-3" />
                <span className="text-sm font-medium text-ink">{t('pick.cta')}</span>
              </label>
            </div>
            <p id={hintId} className="text-xs text-ink-3">
              {t('pick.hint')}
            </p>
          </>
        ) : null}

        {error ? (
          <div role="alert" className="flex flex-col gap-1 rounded-lg border border-danger/40 bg-danger-soft/60 px-3 py-2.5">
            <p className="text-sm font-medium text-danger">{error.title}</p>
            <p dir="auto" className="text-start text-xs text-ink-2">
              {error.detail}
            </p>
          </div>
        ) : null}

        {busy ? (
          <div className="flex flex-col gap-1 py-2">
            <ProgressBar value={progress} label={phase} showValue />
            <p className="sr-only" aria-live="polite">
              {phase}
            </p>
          </div>
        ) : null}

        {info && step === 'ready' ? renderReport(info) : null}
      </div>
    </Dialog>
  );

  // ───────────────────────── report ─────────────────────────

  function renderReport(found: BackupInspectResponse): React.JSX.Element {
    const diff = found.existing?.diff ?? null;
    const lost = diff?.photos.removed ?? 0;
    const nothingChanges =
      diff !== null &&
      !diff.title.changed &&
      !diff.format.changed &&
      !diff.pages.changed &&
      diff.spreads.changed === 0 &&
      diff.photos.added === 0 &&
      diff.photos.removed === 0;

    return (
      <div className="flex flex-col gap-5">
        {/* The verdict. One sentence, before any detail, and the only place a warning is styled. */}
        {diff && diff.losesPhotos ? (
          <div className="flex gap-3 rounded-lg border border-danger/50 bg-danger-soft/60 p-3.5">
            <TriangleAlert aria-hidden className="mt-0.5 size-5 shrink-0 text-danger" />
            <p ref={verdictRef} tabIndex={-1} className="font-display text-lg leading-snug font-semibold text-balance text-danger outline-none">
              {t('verdict.loss', { count: lost })}
            </p>
          </div>
        ) : (
          <p ref={verdictRef} tabIndex={-1} className="font-display text-lg leading-snug text-balance text-ink outline-none">
            {diff === null ? t('fresh.verdict') : nothingChanges ? t('verdict.identical') : t('verdict.changed')}
          </p>
        )}

        {/* Which file this is. Secondary by design: one muted line, never a wall of chips. */}
        <div>
          <p className="k-nikud truncate font-display text-base text-ink">
            {titleOr(found.album.title, t('found.untitled'))}
          </p>
          <p className="text-2xs mt-0.5 text-ink-3">
            {t('found.line', {
              date: formatDate(found.createdAt, locale, { dateStyle: 'medium', timeStyle: 'short' }),
              photos: found.photoCount,
              spreads: found.spreadCount,
            })}
            {file ? (
              <>
                {' · '}
                <bdi>{t('pick.chosen', { name: file.name, size: formatBytes(file.size, locale) })}</bdi>
              </>
            ) : null}
          </p>
        </div>

        {diff ? renderChanges(diff) : null}
        {diff ? renderPhotos(diff) : null}

        <div className="flex flex-col gap-3">
          {diff ? (
            <>
              <OptionCard
                tone="primary"
                title={t('copy.title')}
                body={t('copy.body')}
                action={
                  <Button variant="primary" size="sm" onClick={() => void run('copy')} startIcon={<Copy aria-hidden />}>
                    {t('copy.cta')}
                  </Button>
                }
              />
              <OptionCard
                tone={diff.losesPhotos ? 'danger' : 'plain'}
                title={t('overwrite.title')}
                body={t('overwrite.body')}
                note={diff.losesPhotos ? t('overwrite.loses', { count: lost }) : undefined}
                action={
                  <>
                    {diff.losesPhotos ? (
                      <label htmlFor={ackId} className="flex items-start gap-2 text-sm text-ink">
                        <input
                          id={ackId}
                          type="checkbox"
                          checked={acknowledged}
                          onChange={(e) => setAcknowledged(e.target.checked)}
                          className="mt-0.5 size-4 shrink-0 accent-danger"
                        />
                        <span>{t('overwrite.ack')}</span>
                      </label>
                    ) : null}
                    <Button
                      variant={diff.losesPhotos ? 'danger' : 'secondary'}
                      size="sm"
                      disabled={diff.losesPhotos && !acknowledged}
                      onClick={() => void run('overwrite')}
                    >
                      {t('overwrite.cta')}
                    </Button>
                  </>
                }
              />
            </>
          ) : (
            <OptionCard
              tone="primary"
              title={t('fresh.title')}
              body={t('fresh.body')}
              action={
                <Button variant="primary" size="sm" onClick={() => void run('copy')} startIcon={<PackageOpen aria-hidden />}>
                  {t('fresh.cta')}
                </Button>
              }
            />
          )}

          {/* Quiet third way out, deliberately not a peer of the two real choices. */}
          <button
            type="button"
            onClick={reset}
            className="self-start text-xs text-ink-3 underline underline-offset-2 hover:text-ink-2"
          >
            {t('pick.another')}
          </button>
        </div>
      </div>
    );
  }

  /** Only the fields that actually differ. An unchanged row would be noise the verdict already covered. */
  function renderChanges(diff: BackupDiff): React.JSX.Element | null {
    const rows: { key: string; label: string; from: string; to: string }[] = [];
    if (diff.title.changed) {
      rows.push({
        key: 'name',
        label: t('changes.name'),
        from: titleOr(diff.title.from, t('existingAlbum')),
        to: titleOr(diff.title.to, t('found.untitled')),
      });
    }
    if (diff.format.changed) {
      rows.push({ key: 'format', label: t('changes.format'), from: formatLabel(diff.format.from), to: formatLabel(diff.format.to) });
    }
    if (diff.pages.changed) {
      rows.push({ key: 'pages', label: t('changes.pages'), from: String(diff.pages.from), to: String(diff.pages.to) });
    }

    const spreadsChanged = diff.spreads.changed > 0;
    if (rows.length === 0 && !spreadsChanged) return null;

    const arrow = rtl ? '←' : '→';
    return (
      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold text-ink-2">{t('changes.title')}</h3>
        <ul className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <li key={row.key} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
              <span className="text-xs text-ink-3">{row.label}</span>
              {/* The glyph is decorative; the sentence below carries the direction for a screen reader. */}
              <span className="sr-only">{t('changes.changeLabel', { from: row.from, to: row.to })}</span>
              <span aria-hidden className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-ink-2 line-through">{row.from}</span>
                <span className="text-ink-3">{arrow}</span>
                <span className="font-medium text-ink">{row.to}</span>
              </span>
            </li>
          ))}
          {spreadsChanged ? (
            <li className="text-sm text-ink">
              {t('changes.spreads', { changed: diff.spreads.changed, total: Math.max(diff.spreads.from, diff.spreads.to) })}
            </li>
          ) : null}
        </ul>
      </section>
    );
  }

  /** Says only what is true. Photo loss is the verdict's job, so it is never repeated here. */
  function renderPhotos(diff: BackupDiff): React.JSX.Element | null {
    if (diff.photos.added === 0 && diff.photos.removed === 0) {
      return <p className="text-sm text-ink-2">{t('photos.allPresent', { count: diff.photos.shared })}</p>;
    }
    if (diff.photos.added > 0) {
      return <p className="text-sm text-ink-2">{t('photos.comingBack', { count: diff.photos.added })}</p>;
    }
    return null;
  }
}

/** One choice, with the consequence of taking it written underneath. */
function OptionCard({
  tone,
  title,
  body,
  note,
  action,
}: {
  tone: 'primary' | 'plain' | 'danger';
  title: string;
  body: string;
  note?: string;
  action: React.ReactNode;
}) {
  const border =
    tone === 'primary' ? 'border-accent/60 bg-accent-soft/30' : tone === 'danger' ? 'border-danger/40 bg-danger-soft/30' : 'border-line';
  return (
    <section className={`flex flex-col gap-2 rounded-lg border p-3.5 ${border}`}>
      <div>
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <p className="mt-0.5 text-sm text-ink-2">{body}</p>
        {note ? <p className="mt-1 text-sm font-semibold text-danger">{note}</p> : null}
      </div>
      <div className="flex flex-col items-start gap-2">{action}</div>
    </section>
  );
}
